import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Phone, MapPin, Clock, MessageCircle, Navigation } from "lucide-react";
import { FadeUp, Eyebrow } from "./Reveal";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const SERVICE_OPTIONS = [
    "Roof Installation",
    "Roof Inspection",
    "Roof Repair",
    "Gutter Installation",
    "Gutter Repairs",
    "Roof Damage Repair",
    "Roof Repair for Storm & Wind Damage",
];
const WHATSAPP = "https://wa.me/233553237357?text=Hello%20One%20God%20Roofing,%20I%20would%20like%20to%20request%20a%20roofing%20estimate";
const MAPS_URL = "https://www.google.com/maps/search/?api=1&query=One+God+Roofing+Limited+Company+J.H.+Owusu+Acheampong+St+Sunyani";

const inputCls =
    "w-full bg-white/5 border border-white/10 focus:border-amber-500 text-white placeholder-[#64748B] px-4 py-3.5 text-sm outline-none transition-colors";

export const Contact = ({ presetService }) => {
    const [form, setForm] = useState({ name: "", phone: "", service: "", location: "", notes: "" });
    const [sending, setSending] = useState(false);

    useEffect(() => {
        if (presetService) setForm((f) => ({ ...f, service: presetService }));
    }, [presetService]);

    const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

    const submit = async (e) => {
        e.preventDefault();
        setSending(true);
        try {
            await axios.post(`${API}/quotes`, form);
            toast.success("Quote request sent — we will call you back shortly.", {
                description: `${form.name} · ${form.service}`,
            });
            setForm({ name: "", phone: "", service: "", location: "", notes: "" });
        } catch {
            toast.error("Could not send your request. Please call us instead: 055 323 7357.");
        } finally {
            setSending(false);
        }
    };

    return (
        <section id="contact" data-testid="quote-contact-section" className="py-24 sm:py-32">
            <div className="max-w-7xl mx-auto px-5 sm:px-8">
                <FadeUp className="mb-14">
                    <Eyebrow>Free on-site quote</Eyebrow>
                    <h2 className="font-display font-bold uppercase text-4xl sm:text-6xl leading-[1.02] mt-5">
                        Tell us about<br />
                        <span className="text-outline">your roof.</span>
                    </h2>
                </FadeUp>

                <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16">
                    <FadeUp delay={0.1} className="flex flex-col gap-4">
                        <a href={MAPS_URL} target="_blank" rel="noopener noreferrer" data-testid="contact-address-link" className="group flex gap-4 border border-white/10 bg-[#13191E] hover:border-amber-500/50 p-6 transition-colors">
                            <MapPin size={20} className="text-amber-500 shrink-0 mt-0.5" />
                            <div>
                                <p className="font-display font-bold uppercase text-[#F8FAFC]">Visit or get directions</p>
                                <p className="text-sm text-[#94A3B8] mt-1">J.H. Owusu Acheampong St, Sunyani — Bono Region, Ghana</p>
                                <span className="inline-flex items-center gap-1.5 font-mono2 text-[10px] uppercase tracking-[0.2em] text-amber-500 mt-3">
                                    <Navigation size={11} /> Open in Google Maps
                                </span>
                            </div>
                        </a>

                        <div className="flex gap-4 border border-white/10 bg-[#13191E] p-6">
                            <Clock size={20} className="text-amber-500 shrink-0 mt-0.5" />
                            <div>
                                <p className="font-display font-bold uppercase text-[#F8FAFC]">Opening hours</p>
                                <p className="text-sm text-[#10B981] mt-1 flex items-center gap-2">
                                    <span className="w-1.5 h-1.5 rounded-full bg-[#10B981]" /> Open · Closes 5 pm
                                </p>
                            </div>
                        </div>

                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                            <a href="tel:+233553237357" data-testid="contact-phone-0553237357" className="bg-amber-500 hover:bg-amber-400 text-[#0F172A] p-6 transition-colors">
                                <Phone size={18} className="mb-3" />
                                <p className="font-mono2 text-[10px] uppercase tracking-[0.2em] opacity-70">Primary hotline</p>
                                <p className="font-display font-bold text-xl mt-1">055 323 7357</p>
                            </a>
                            <a href="tel:+233279912807" data-testid="contact-phone-0279912807" className="border border-white/15 hover:border-amber-500 bg-[#13191E] p-6 transition-colors group">
                                <Phone size={18} className="mb-3 text-amber-500" />
                                <p className="font-mono2 text-[10px] uppercase tracking-[0.2em] text-[#64748B]">Direct line</p>
                                <p className="font-display font-bold text-xl mt-1 text-white group-hover:text-amber-500 transition-colors">027 991 2807</p>
                            </a>
                        </div>

                        <a href={WHATSAPP} target="_blank" rel="noopener noreferrer" data-testid="contact-whatsapp-direct" className="flex items-center justify-center gap-2.5 border border-[#10B981]/40 hover:bg-[#10B981]/10 text-[#10B981] font-semibold py-4 text-sm transition-colors">
                            <MessageCircle size={17} />
                            Chat with us on WhatsApp
                        </a>
                    </FadeUp>

                    <FadeUp delay={0.2}>
                        <form
                            data-testid="quote-request-form"
                            onSubmit={submit}
                            className="border border-white/10 bg-[#13191E] p-6 sm:p-8 flex flex-col gap-5"
                        >
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                                <label className="block">
                                    <span className="font-mono2 text-[10px] uppercase tracking-[0.2em] text-[#94A3B8] block mb-2">Full name *</span>
                                    <input required value={form.name} onChange={set("name")} data-testid="quote-input-name" placeholder="Kwame Owusu" className={inputCls} />
                                </label>
                                <label className="block">
                                    <span className="font-mono2 text-[10px] uppercase tracking-[0.2em] text-[#94A3B8] block mb-2">Phone number *</span>
                                    <input required type="tel" value={form.phone} onChange={set("phone")} data-testid="quote-input-phone" placeholder="055 000 0000" className={inputCls} />
                                </label>
                            </div>

                            <label className="block">
                                <span className="font-mono2 text-[10px] uppercase tracking-[0.2em] text-[#94A3B8] block mb-2">Service needed *</span>
                                <select required value={form.service} onChange={set("service")} data-testid="quote-select-service" className={inputCls}>
                                    <option value="" disabled>
                                        Choose a service…
                                    </option>
                                    {SERVICE_OPTIONS.map((s) => (
                                        <option key={s} value={s}>
                                            {s}
                                        </option>
                                    ))}
                                </select>
                            </label>

                            <label className="block">
                                <span className="font-mono2 text-[10px] uppercase tracking-[0.2em] text-[#94A3B8] block mb-2">Your town / area *</span>
                                <input required value={form.location} onChange={set("location")} data-testid="quote-input-location" placeholder="e.g. Sunyani, Fiapre, Abesim…" className={inputCls} />
                            </label>

                            <label className="block">
                                <span className="font-mono2 text-[10px] uppercase tracking-[0.2em] text-[#94A3B8] block mb-2">About the job</span>
                                <textarea rows={4} value={form.notes} onChange={set("notes")} data-testid="quote-textarea-notes" placeholder="Leaking ridge after the rains, two-bedroom house…" className={`${inputCls} resize-none`} />
                            </label>

                            <button
                                type="submit"
                                data-testid="quote-form-submit-button"
                                disabled={sending}
                                className="bg-amber-500 hover:bg-amber-400 disabled:opacity-60 text-[#0F172A] font-semibold py-4 text-sm transition-colors mt-1"
                            >
                                {sending ? "Sending…" : "Request my free quote"}
                            </button>
                            <p className="font-mono2 text-[10px] uppercase tracking-[0.15em] text-[#64748B] text-center">
                                We reply with a call — no obligations
                            </p>
                        </form>
                    </FadeUp>
                </div>
            </div>
        </section>
    );
};

export default Contact;
